/**
 * The game detail header in the face-off layout of the Scores UX spec v1.0
 * section 7.1, after the legacy app's scoreboard.
 *
 *   nav row:    [back 44]  "KC at BUF" (h1)              [bell 44][share 44]
 *   scoreboard: [away col, flex 1] [away score] [center, min 112] [home score] [home col, flex 1]
 *
 * Away on the left, home on the right, in DOM and reading order. Each team
 * column is one link into that team's profile (ISSUE-041 R1): a 48px round
 * mark, the name on one line (the longest server name that fits the column,
 * `pickTeamLabel`, never an ellipsis), then the record in `small`. The
 * scores are `display` 32/800 sans tabular figures, never mono; on a final the
 * loser's score is `textSecondary` at the same weight, and the winner's name
 * is bold. Scheduled, postponed and canceled games have no score slots at
 * all, so a pre-game header shows no digits.
 *
 * The center column is the server's words only: a scheduled game's kickoff
 * line (`statusLabel`) in `h2` over the long day; a live game's `statusLabel`
 * (period and clock) in `h2` over a red "Live" pill, the football down and
 * distance or the baseball bases and outs glyph (visible, not screen reader
 * only); a final's "Final" or "Final/OT" over the day; a postponed, canceled
 * or delayed game's label in `caption`, `warning` when delayed.
 *
 * The header sits on `bg`, no card, inside the page gutter the shell already
 * draws, 12 above and 8 below the scoreboard.
 *
 * Fit at 375: a team column never gets narrower than its 48px mark
 * (`min-w-game-header-mark-size`), so a mark can never spill over a score.
 * Two 3-digit basketball scores ("112", "108") at 32/800 take about 60 each,
 * so on a phone the four gaps are 4 (8 from 640) and the center column has no
 * 112 floor: it takes its content's width and wraps its label at spaces. From
 * 640 up the center keeps its 112 floor.
 *
 * Collapse: past 80px of scroll a 48px compact bar is pinned to the top of
 * the viewport, "[mark] KC 10 · Q3 08:41 · 14 BUF [mark]", on `surface` with a
 * bottom hairline, fading in over `motion-mid` (none under reduced motion).
 * It is `position: fixed`, so it never moves the page, and the game's tab row
 * sticks under it (`GAME_DETAIL_TABS_STICKY_CLASS`). It is mounted only while
 * shown, so its repeat of the score is never read twice, and it carries no
 * actions.
 *
 * `Alerts` opens the real per-game alerts overlay (ISSUE-183). `Share` puts
 * this game's id into `?shareEventId=` and opens the picker mounted once on
 * the `/scores` route (ISSUE-1085).
 *
 * Emphasis reads the server's `winnerTeamId` / `isTie` and the live treatment
 * reads its `isLive` flag; this file computes no winner and builds no clock.
 */
import { tokens } from "@sportsmet/theme";
import { estimateLabelWidthPx, pickTeamLabel } from "@sportsmet/theme/scores";
import { cn, FOCUS_RING } from "@sportsmet/ui";
import { Link } from "@tanstack/react-router";
import { type ReactNode, useEffect, useState } from "react";

import {
  BellIcon,
  ICON_BUTTON_CLASS,
  ShareIcon,
} from "../../icons/actionIcons";
import { useOverlay } from "../../routeState/useOverlay";
import { GameAlertsDialog } from "../gameAlerts/GameAlertsDialog";
import { BasesDiamond } from "../liveState/BasesDiamond";
import type { typeLiveStateWire } from "../liveState/liveStateTypes";
import { OutsDots } from "../liveState/OutsDots";
import { PowerPlayBadge } from "../liveState/PowerPlayBadge";
import { longDayLabel, todayInZone } from "../scores/calendarDay";
import { DetailNavBar } from "../scores/DetailNavBar";
import { TeamDisc } from "../scores/TeamDisc";
import { useMeasuredWidth, viewportWidthPx } from "../scores/useMeasuredWidth";
import {
  GAME_DETAIL_BACK_LABEL,
  GAME_DETAIL_LIVE_PILL_LABEL,
  gameDetailTitle,
  HEADER_COLLAPSE_THRESHOLD_PX,
} from "./gameDetailLogic";
import { resolvePlatePair } from "./teamPlateText";

export type typeGameDetailHeaderStatus =
  "scheduled" | "inProgress" | "final" | "postponed" | "canceled" | "delayed";

export type typeGameDetailHeaderTeam = {
  teamId: string;
  name: string;
  /** The nickname or school, once events-api sends it (spec 1.4). */
  shortName?: string | null;
  abbreviation: string;
  score: number | null;
  recordLabel: string | null;
  /** The resolved mark URL from /event/detail-get, or null when this team has
   *  no mark on file. The disc falls back to the plate (ISSUE-902 R5). */
  logoUrl?: string | null;
  /** The plate fill when there is no logo. */
  primaryColorHex?: string | null;
};

export const ALERTS_SHEET = "alerts";
export const SHARE_DIALOG = "share";

/** The game's tab row sticks right under the 48px compact bar. */
export const GAME_DETAIL_TABS_STICKY_CLASS = "sticky top-12 z-10";

/** The center column's floor from 640 up (spec 7.1), and the score slot's.
 *  On a phone the center has no floor; 112 stays the first render's guess. */
const CENTER_COLUMN_PX = 112;
const SCORE_SLOT_PX = 48;
const NAME_FONT_PX = tokens.text.bodyStrong.size;
const SCORE_FONT_PX = tokens.text.display.size;
/** The width from which the scoreboard widens its gaps and the center
 *  column takes its 112 floor. Tailwind's `sm`. */
const WIDE_MIN_VIEWPORT_PX = 640;

/** The gap between the scoreboard's five slots: `gap-1` (4) on a phone,
 *  `sm:gap-2` (8) from 640. */
export function gameHeaderGapPx(viewportPx: number): number {
  return viewportPx >= WIDE_MIN_VIEWPORT_PX
    ? tokens.space.xs
    : tokens.space.xxs;
}

type typeEmphasis = "win" | "lose" | "neutral";

function emphasisFor(
  status: typeGameDetailHeaderStatus,
  isTie: boolean,
  winnerTeamId: string | null,
  teamId: string,
): typeEmphasis {
  if (status !== "final" || isTie || winnerTeamId === null) return "neutral";
  return winnerTeamId === teamId ? "win" : "lose";
}

/** Scheduled, postponed and canceled games draw no score slots (spec 7.1). */
export function headerShowsScores(status: typeGameDetailHeaderStatus): boolean {
  return status === "inProgress" || status === "final" || status === "delayed";
}

/** One score slot's width: its 48 floor, or the score's estimated width at
 *  `display` 32/800 when that is wider (three digits are about 58). */
function scoreSlotPx(score: number | null): number {
  return Math.max(
    SCORE_SLOT_PX,
    estimateLabelWidthPx(String(score ?? ""), SCORE_FONT_PX),
  );
}

/**
 * One team column's width at a viewport: the page column less the gutters,
 * the center column (`centerPx`, 112 when unknown), the score slots and the
 * gaps, split in two. The first render's guess before the name is measured,
 * and the arithmetic the 375 fit test checks against the 48px mark.
 */
export function gameHeaderTeamColumnPx(props: {
  viewportPx: number;
  scores: { away: number | null; home: number | null } | null;
  centerPx?: number;
}): number {
  const viewport = Math.min(props.viewportPx, tokens.size.contentWidth);
  const scores =
    props.scores === null
      ? 0
      : scoreSlotPx(props.scores.away) + scoreSlotPx(props.scores.home);
  const gaps =
    (props.scores === null ? 2 : 4) * gameHeaderGapPx(props.viewportPx);
  return Math.max(
    0,
    (viewport -
      2 * tokens.size.pageGutter -
      (props.centerPx ?? CENTER_COLUMN_PX) -
      scores -
      gaps) /
      2,
  );
}

function TeamColumn(props: {
  team: typeGameDetailHeaderTeam;
  emphasis: typeEmphasis;
  side: "away" | "home";
  plateText: string;
  scores: { away: number | null; home: number | null } | null;
}): ReactNode {
  const { team, side } = props;
  const testId = `game-detail-team-${side}`;
  const { ref, widthPx } = useMeasuredWidth<HTMLSpanElement>(
    gameHeaderTeamColumnPx({
      viewportPx: viewportWidthPx(tokens.size.contentWidth),
      scores: props.scores,
    }),
  );
  const label = pickTeamLabel(
    {
      name: team.name,
      shortName: team.shortName ?? null,
      abbreviation: team.abbreviation,
    },
    widthPx,
    NAME_FONT_PX,
  );
  return (
    <div
      className="flex min-w-game-header-mark-size flex-1 flex-col items-stretch"
      data-testid={testId}
      data-emphasis={props.emphasis}
    >
      <Link
        to="/scores"
        // `from` keeps `prev` narrowed to the /scores search shape, so the
        // cleared keys typecheck. The /scores route renders game detail
        // whenever `eventId` is present, so the link must clear it.
        from="/scores"
        search={(prev) => ({
          ...prev,
          teamId: team.teamId,
          eventId: undefined,
          tab: undefined,
        })}
        aria-label={team.name}
        className={cn(
          "flex min-h-touch-target min-w-0 flex-col items-center gap-2 rounded-sm text-ink no-underline",
          FOCUS_RING,
        )}
        data-testid={`${testId}-link`}
      >
        <TeamDisc
          plateText={props.plateText}
          logoUrl={team.logoUrl ?? null}
          plateColorHex={team.primaryColorHex ?? null}
          size="header"
          testId="team-mark"
          imageTestId="team-mark-image"
        />
        <span className="flex w-full min-w-0 flex-col items-center">
          <span
            ref={ref}
            className={cn(
              "w-full overflow-hidden whitespace-nowrap text-center text-body-strong",
              props.emphasis === "lose" ? "text-ink-secondary" : "text-ink",
            )}
            data-testid={`${testId}-name`}
            data-full-name={team.name}
          >
            {label}
          </span>
          {team.recordLabel !== null ? (
            <span
              className="text-small text-ink-tertiary tabular-nums"
              data-testid={`${testId}-record`}
            >
              {team.recordLabel}
            </span>
          ) : null}
        </span>
      </Link>
    </div>
  );
}

function ScoreSlot(props: {
  score: number | null;
  emphasis: typeEmphasis;
  side: "away" | "home";
  hasPossession: boolean;
  /** ISSUE-1393. The server's "PP" when this hockey team is on a power play. */
  powerPlayLabel: string | null;
}): ReactNode {
  return (
    <div
      className="flex min-w-12 shrink-0 flex-col items-center gap-1"
      data-testid={`game-detail-score-slot-${props.side}`}
    >
      {/* The mark is 48 tall; the score centers on it. */}
      <span className="flex h-game-header-mark-size items-center">
        <span
          className={cn(
            "text-display tabular-nums",
            props.emphasis === "lose" ? "text-ink-secondary" : "text-ink",
          )}
          data-testid={`game-detail-score-${props.side}`}
        >
          {props.score ?? ""}
        </span>
      </span>
      {props.hasPossession ? (
        <span
          className="h-score-live-dot w-score-live-dot shrink-0 rounded-full bg-primary-solid"
          data-testid="possession-dot"
          role="img"
          aria-label="has the ball"
        />
      ) : null}
      {props.powerPlayLabel !== null ? (
        <PowerPlayBadge label={props.powerPlayLabel} />
      ) : null}
    </div>
  );
}

function LivePill(): ReactNode {
  return (
    <span
      className="inline-flex h-6 items-center gap-1 rounded-full bg-error-tint px-2 text-caption font-bold text-error"
      data-testid="game-detail-live-pill"
    >
      <span
        className="h-score-live-dot w-score-live-dot shrink-0 rounded-full bg-error-solid"
        data-testid="game-detail-live-dot"
        aria-hidden="true"
      />
      {GAME_DETAIL_LIVE_PILL_LABEL}
    </span>
  );
}

function LiveGlyph(props: { liveState: typeLiveStateWire }): ReactNode {
  const baseball = props.liveState.baseball ?? null;
  const downAndDistance = props.liveState.downAndDistanceLabel ?? null;
  return (
    <>
      {downAndDistance !== null && downAndDistance !== "" ? (
        <span
          className="text-caption text-ink tabular-nums"
          data-testid="game-detail-down-and-distance"
        >
          {downAndDistance}
        </span>
      ) : null}
      {baseball !== null ? (
        <span
          className="flex items-center gap-2"
          data-testid="game-detail-baseball-glyph"
          aria-hidden="true"
        >
          <BasesDiamond
            isFirstOccupied={baseball.isFirstOccupied}
            isSecondOccupied={baseball.isSecondOccupied}
            isThirdOccupied={baseball.isThirdOccupied}
            size="panel"
          />
          <OutsDots outs={baseball.outs} size="row" />
        </span>
      ) : null}
      <span className="sr-only" data-testid="game-detail-live-state-text">
        {props.liveState.summaryLabel}
      </span>
    </>
  );
}

function CenterColumn(props: {
  status: typeGameDetailHeaderStatus;
  statusLabel: string;
  isLiveShown: boolean;
  dayLabel: string | null;
  liveState: typeLiveStateWire | null;
}): ReactNode {
  const { status, statusLabel } = props;
  const dayLine =
    props.dayLabel !== null ? (
      <span
        className="text-small text-ink-secondary"
        data-testid="game-detail-day"
      >
        {props.dayLabel}
      </span>
    ) : null;
  let content: ReactNode;
  if (props.isLiveShown) {
    content = (
      <>
        <span
          className="text-h2 text-ink tabular-nums"
          data-testid="game-detail-state"
        >
          {statusLabel}
        </span>
        <LivePill />
        {props.liveState !== null ? (
          <LiveGlyph liveState={props.liveState} />
        ) : null}
      </>
    );
  } else if (status === "scheduled") {
    content = (
      <>
        <span
          className="text-h2 text-ink tabular-nums"
          data-testid="game-detail-state"
        >
          {statusLabel}
        </span>
        {dayLine}
      </>
    );
  } else if (status === "final") {
    content = (
      <>
        <span className="text-caption text-ink" data-testid="game-detail-state">
          {statusLabel}
        </span>
        {dayLine}
      </>
    );
  } else {
    content = (
      <span
        className={cn(
          "text-caption",
          status === "delayed" ? "text-warning" : "text-ink-secondary",
        )}
        data-testid="game-detail-state"
      >
        {statusLabel}
      </span>
    );
  }
  return (
    <div
      className="flex min-w-0 flex-col items-center gap-1 pt-2 text-center sm:min-w-28 sm:shrink-0"
      data-testid="game-detail-center"
    >
      {content}
    </div>
  );
}

function CompactBar(props: {
  away: typeGameDetailHeaderTeam;
  home: typeGameDetailHeaderTeam;
  awayText: string;
  homeText: string;
  statusLabel: string;
  hasScores: boolean;
}): ReactNode {
  const { away, home } = props;
  const middle = props.hasScores
    ? `${String(away.score ?? "")} · ${props.statusLabel} · ${String(home.score ?? "")}`
    : props.statusLabel;
  return (
    <div
      className="fixed inset-x-0 top-0 z-20 animate-fade-in border-b border-hairline bg-surface motion-reduce:animate-none"
      data-testid="game-detail-header-compact"
    >
      <div className="sm-content-width flex h-control-height items-center justify-center gap-2 px-page-gutter">
        <TeamDisc
          plateText={props.awayText}
          logoUrl={away.logoUrl ?? null}
          plateColorHex={away.primaryColorHex ?? null}
          size="mini"
          testId="team-mark"
          imageTestId="team-mark-image"
        />
        <span className="text-body-strong text-ink">{away.abbreviation}</span>
        <span
          className="text-caption text-ink-secondary tabular-nums"
          data-testid="game-detail-compact-middle"
        >
          {middle}
        </span>
        <span className="text-body-strong text-ink">{home.abbreviation}</span>
        <TeamDisc
          plateText={props.homeText}
          logoUrl={home.logoUrl ?? null}
          plateColorHex={home.primaryColorHex ?? null}
          size="mini"
          testId="team-mark"
          imageTestId="team-mark-image"
        />
      </div>
    </div>
  );
}

export const GameDetailHeader = ({
  eventId,
  status,
  statusLabel,
  isLive,
  home,
  away,
  winnerTeamId,
  isTie,
  liveState = null,
  kickoffAt,
  timeZone,
  nowMs,
}: {
  eventId: string;
  status: typeGameDetailHeaderStatus;
  statusLabel: string;
  isLive: boolean;
  home: typeGameDetailHeaderTeam;
  away: typeGameDetailHeaderTeam;
  winnerTeamId: string | null;
  isTie: boolean;
  /** ISSUE-1091. The server's live game state; drawn only while in progress. */
  liveState?: typeLiveStateWire | null;
  /** The kickoff instant, for the long day under a scheduled or final game. */
  kickoffAt?: string | undefined;
  timeZone?: string | undefined;
  /** "Now", for the "is this year" choice in the long day. Tests pin it. */
  nowMs?: number | undefined;
}): ReactNode => {
  const overlay = useOverlay();
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    const onScroll = (): void => {
      setIsCollapsed(window.scrollY > HEADER_COLLAPSE_THRESHOLD_PX);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const isLiveShown = status === "inProgress" && isLive;
  const shownLiveState = status === "inProgress" ? liveState : null;
  const possessionTeamId = shownLiveState?.possessionTeamId ?? null;
  const hockey = shownLiveState?.hockey ?? null;
  const powerPlayLabelFor = (teamId: string): string | null =>
    hockey !== null && hockey.powerPlayTeamId === teamId
      ? hockey.powerPlayLabel
      : null;
  const hasScores = headerShowsScores(status);
  const scores = hasScores ? { away: away.score, home: home.score } : null;
  const homeEmphasis = emphasisFor(status, isTie, winnerTeamId, home.teamId);
  const awayEmphasis = emphasisFor(status, isTie, winnerTeamId, away.teamId);
  // Resolved once for the pair, so the compact bar and the scoreboard cannot
  // disagree about the same game (ISSUE-902 R4).
  const { awayText, homeText } = resolvePlatePair({
    away: { name: away.name, abbreviation: away.abbreviation },
    home: { name: home.name, abbreviation: home.abbreviation },
  });
  const dayLabel =
    kickoffAt !== undefined &&
    timeZone !== undefined &&
    !Number.isNaN(Date.parse(kickoffAt))
      ? longDayLabel(
          todayInZone({ nowMs: Date.parse(kickoffAt), timeZone }),
          todayInZone({ nowMs: nowMs ?? Date.now(), timeZone }),
        )
      : null;

  return (
    <header className="flex flex-col" data-testid="game-detail-header">
      <DetailNavBar
        backLabel={GAME_DETAIL_BACK_LABEL}
        clearKeys={["eventId", "tab", "lineupTeam"]}
        title={gameDetailTitle({
          awayAbbreviation: away.abbreviation,
          homeAbbreviation: home.abbreviation,
        })}
        testId="game-detail-nav"
        actions={
          <div
            className="flex shrink-0 items-center"
            data-testid="game-detail-header-actions"
          >
            <button
              type="button"
              aria-label="Alerts"
              data-testid="game-detail-alerts"
              className={cn(ICON_BUTTON_CLASS, "text-ink-secondary")}
              onClick={() => {
                overlay.open({ kind: "sheet", name: ALERTS_SHEET });
              }}
            >
              <BellIcon />
            </button>
            <button
              type="button"
              aria-label="Share"
              data-testid="game-detail-share"
              className={cn(ICON_BUTTON_CLASS, "text-ink-secondary")}
              onClick={() => {
                overlay.open({
                  kind: "dialog",
                  name: SHARE_DIALOG,
                  params: { shareEventId: eventId },
                });
              }}
            >
              <ShareIcon />
            </button>
          </div>
        }
      />
      <div
        className="flex items-start gap-1 pt-3 pb-2 sm:gap-2"
        data-testid="game-detail-header-full"
      >
        <TeamColumn
          team={away}
          emphasis={awayEmphasis}
          side="away"
          plateText={awayText}
          scores={scores}
        />
        {hasScores ? (
          <ScoreSlot
            score={away.score}
            emphasis={awayEmphasis}
            side="away"
            hasPossession={possessionTeamId === away.teamId}
            powerPlayLabel={powerPlayLabelFor(away.teamId)}
          />
        ) : null}
        <CenterColumn
          status={status}
          statusLabel={statusLabel}
          isLiveShown={isLiveShown}
          dayLabel={dayLabel}
          liveState={shownLiveState}
        />
        {hasScores ? (
          <ScoreSlot
            score={home.score}
            emphasis={homeEmphasis}
            side="home"
            hasPossession={possessionTeamId === home.teamId}
            powerPlayLabel={powerPlayLabelFor(home.teamId)}
          />
        ) : null}
        <TeamColumn
          team={home}
          emphasis={homeEmphasis}
          side="home"
          plateText={homeText}
          scores={scores}
        />
      </div>
      {isCollapsed ? (
        <CompactBar
          away={away}
          home={home}
          awayText={awayText}
          homeText={homeText}
          statusLabel={statusLabel}
          hasScores={hasScores}
        />
      ) : null}

      <GameAlertsDialog
        eventId={eventId}
        open={overlay.isOpen("sheet", ALERTS_SHEET)}
        onClose={() => {
          overlay.close({ kind: "sheet" });
        }}
      />
    </header>
  );
};
